import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import {DataFrameFilterOptions} from 'data-frames/filters/DataFrameFilterOptions';
import {
  DataFrameFilter,
  DataFrameFilterOperation,
  DataFrameFilterStepConfig,
} from 'data-frames/filters/DataFrameFilterTypes';
import {ColumnHeaderTransformOptions} from 'data-frames/header-menu/DataFrameHeaderMenuOptions';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';

/**
 * `DataFrameFilterOptions` to use if steps are running or failed.
 * @package
 */
export const DATA_FRAME_FILTER_OPTIONS_FOR_IN_PROGRESS_STEP: DataFrameFilterOptions = {
  getFilterForColumnHeaders: () => null,
  // oxlint-disable-next-line require-await
  updateFilterForColumn: async () => undefined,
  dataFrameSpec: {columns: [], name: 'Unknown'},
  getUnfilteredColumns: () => [],
};

/** Get filters for all columns, from a given step config. */
function getAllFiltersFromFilterStepConfig(
  config: PipelineStepConfig<PipelineStepType.FILTER_DATASET, 0> | null
): Array<DataFrameFilter> {
  return (config as DataFrameFilterStepConfig | null)?.filter?.filters ?? [];
}

/** Check if a filter is for a particular column. */
function filterMatchesColumn(filter: DataFrameFilter, columnSystemName: string): boolean {
  return filter.filters.every((filterCondition) => filterCondition.columnSystemName === columnSystemName);
}

/** Get the filter for a specific column on a specific step. */
function getFilterForColumnSystemName(
  config: PipelineStepConfig<PipelineStepType.FILTER_DATASET, 0> | null,
  columnSystemName: string
): DataFrameFilter | null {
  return (
    getAllFiltersFromFilterStepConfig(config).find((filter) =>
      filterMatchesColumn(filter, columnSystemName)
    ) ?? null
  );
}

/** Get columns which are not filtered on the current filter step.  */
function getUnfilteredColumns(
  dataFrameSpec: DataFrameSpec,
  config: PipelineStepConfig<PipelineStepType.FILTER_DATASET, 0> | null
): DataFrameSpec['columns'] {
  return dataFrameSpec.columns.filter(
    ({systemName}) => getFilterForColumnSystemName(config, systemName) == null
  );
}

/**
 * Get `DataFrameFilterOptions`, used to inject logic for filtering columns into a data frame table.
 *
 * @package
 */
export default function getFilterColumnOptions(
  dataFrameSpec: DataFrameSpec,
  currentConfig: PipelineStepConfig<PipelineStepType.FILTER_DATASET, 0> | null,
  updateConfig: (newConfig: PipelineStepConfig<PipelineStepType.FILTER_DATASET, 0>) => Promise<void>,
  deleteStep: () => Promise<void>
): ColumnHeaderTransformOptions[PipelineStepType.FILTER_DATASET] {
  const updateFilterForColumn = async (
    columnSystemName: string,
    filter: DataFrameFilter | null
  ): Promise<void> => {
    if (currentConfig) {
      const filtersToKeep = getAllFiltersFromFilterStepConfig(currentConfig).filter(
        (filter) => !filterMatchesColumn(filter, columnSystemName)
      );
      const updatedFilters = filter == null ? filtersToKeep : [...filtersToKeep, filter];

      if (updatedFilters.length > 0) {
        await updateConfig({filter: {operation: DataFrameFilterOperation.AND, filters: updatedFilters}});
      } else {
        await deleteStep();
      }
    } else if (filter != null) {
      await updateConfig({filter: {operation: DataFrameFilterOperation.AND, filters: [filter]}});
    }
  };

  return {
    getFilterForColumnHeaders: (columnSystemName) =>
      getFilterForColumnSystemName(currentConfig, columnSystemName),
    updateFilterForColumn,
    dataFrameSpec,
    getUnfilteredColumns: () => getUnfilteredColumns(dataFrameSpec, currentConfig),
  };
}
